import { ReactNode } from 'react';
import { FormName } from '../core/formRegistry';
import { useDynamicField } from '../core/useDynamicField';
import {
    Wrapper,
    Label,
    StyledInput,
    ErrorBubble,
    ErrorContent,
    ErrorIconWrapper,
} from './CustomInput.style';

type CustomInputProps = {
    name: string;
    formName: FormName;
    field: string;
    debounce?: boolean;
    required?: boolean;
    disabled?: boolean;
    fieldClassName?: string;
    errorClassName?: string;
    errorIcon?: ReactNode;
    label?: string;
    maxLen?: number;
    digitsOnly?: boolean;
    patchValue?: (value: string) => string;
    patchBeforeChange?: (value: string) => string;
    customCallback?: (value: string) => void;
    onBlur?: () => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name' | 'value' | 'onBlur'>;

export const CustomInput = ({
    name,
    field,
    formName,
    debounce = false,
    label,
    required,
    disabled,
    className,
    fieldClassName,
    errorClassName,
    errorIcon,
    maxLen,
    patchValue,
    digitsOnly,
    patchBeforeChange,
    customCallback,
    onBlur: customOnBlur,
    ...rest
}: CustomInputProps) => {
    const f = useDynamicField(formName, field);

    return (
        <Wrapper className={className}>
            {!!label && (
                <Label htmlFor={name} $required={!!required}>
                    {label}
                </Label>
            )}
            <StyledInput
                id={name}
                name={name}
                $disabled={!!disabled}
                value={patchValue ? patchValue(f.value) : f.value}
                onChange={(e) => {
                    const newValue = patchBeforeChange
                        ? patchBeforeChange(e.target.value)
                        : e.target.value;

                    if (maxLen && newValue.length > maxLen) return;
                    if (digitsOnly && Number.isNaN(+newValue)) return;

                    f.onChange(newValue, debounce);
                    customCallback?.(newValue);
                }}
                onBlur={() => {
                    f.onBlur();
                    customOnBlur?.();
                }}
                className={fieldClassName}
                autoComplete={name}
                {...rest}
            />
            {f.touched && f.error && (
                <ErrorBubble className={errorClassName}>
                    <ErrorContent>
                        {!!errorIcon && <ErrorIconWrapper>{errorIcon}</ErrorIconWrapper>}
                        <span>{f.error}</span>
                    </ErrorContent>
                </ErrorBubble>
            )}
        </Wrapper>
    );
};
